<script lang="ts" setup>
import { ButtonComp, IconComp, type ButtonTypes } from '@/components';
import type { IIcon, IIconPosition, ITranslationkey } from '@/components/Interfaces';
import { computed, nextTick, onBeforeUpdate, onMounted, onUnmounted, unref, watch } from 'vue';
import { ref } from 'vue';
import { Dropdown } from 'bootstrap';
import type { ComponentPublicInstance } from 'vue';
import type { IDropDownExposed } from './IDropDownExposed';
import { DropdownItemButton, DropdownItemMenu, type DropdownItemTypes, DropdownItemGroup, DropdownItemSeparator } from '@/objects';
import DropdownCompItemRenderer from './DropdownCompItemRenderer.vue';
import type { TaskLoaderState } from '@/composeables';
import type { Ref } from 'vue';
import { useElementSize } from '@vueuse/core';

const props = withDefaults(
    defineProps<
        {
            showArrow?: boolean;

            /**
             * Sets the autoclose behavior of the dropdown
             *
             * If not explicitly set, it will default to 'outside' if there is a submenu in the dropdownitems (at the creation time of the dropdown instance)
             * and true otherwise.
             * Explicitly set value will always override the default value. (Note that a submenu wont work with autoClose != 'outside' tho)
             *
             * Note: If you have a checkbox inside the dropdown, and you click the checkbox label, the dropdown will close if autoClose is set to true (which is the default)
             * Maybe we should change the default to inside? Basically we only want the true behaviour if this is a select style thing with a bunch of buttons
             */
            autoClose?: 'inside' | 'outside' | true | false;

            /**
             * If the dropdown should use columns
             * @default undefined No columns
             */
            columns?: 1 | 2 | 3 | 4 | 5;

            /**
             * The type of button
             */
            buttonType?: ButtonTypes;

            /**
             * The items in the dropdown
             */
            dropdownItems?: DropdownItemTypes[];

            /**
             * Puts the dropdown into Split mode if supplied.
             * This will be the action for the triggering button.
             * The dropdown items will be in the dropdown menu which can be opened with a split button to the right of the triggering button.
             */
            splitAction?: DropdownItemButton;

            /**
             * Text to show if the dropdown does not have any items
             */
            emptytext?: string;

            /**
             * If the input is disabled
             * @default undefined
             */
            disabled?: boolean;

            /**
             * This will show a loader in the button and disable the button
             * @default null
             */
            loadstate?: TaskLoaderState | null;

            /**
             * if we should show a badge with the number of items in the dropdown
             * @default false
             */
            showItemCountBadge?: boolean;

            /**
             * The maximum items to render at any given time
             * If this is set the dropdown uses a virtual scroller to only render some of the items.
             * A reasonable number to put here if you have alot of items is 25
             */
            virtualScrollerMaxItems?: number;

            /**
             * Internal classes to apply to the dropdown wrapper
             */
            classes?: string;

            /**
             * If we have some items that we want to mark (for example to show that they are selected), we can pass them in here and they will get a different background color. This is used for example in the typeahead dropdown to show the currently selected item(s).
             */
            markValues?: DropdownItemTypes[];

            /**
             * If true, the dropdown will automatically open when rendered
             */
            autoOpen?: boolean;

            /**
             * is this a submenu
             */
            isSubmenu?: boolean;
        } & IIcon &
            IIconPosition &
            ITranslationkey
    >(),
    {
        showArrow: true,
        autoClose: undefined,
        columns: undefined,
        buttonType: 'Secondary',
        showItemCountBadge: false,
        autoOpen: false,
    },
);

/**
 * Wrapper div for the whole dropdown including trigger and menu
 */
const dropdownelement = ref<HTMLElement | null>(null);

/**
 * we cant just get width as it is not reactive on resize, we use this to set min width of the actual dropdown
 */
const { width: dropdownelementWidth } = useElementSize(dropdownelement);

/**
 * The ul that contains all the menu items (and the virtual scroller)
 */
const dropdownMenuUl = ref<HTMLUListElement | null>(null);

// Bootstrap creates a dropdown instance for the element that has the data-bs-toggle="dropdown" attribute
// So if this element exists, we want to create the dropdown instance for this element
// If it doesnt exist, we want to create the dropdown instance for the dropdown wrapper itself, and the dropdown will be used programatically
// TODO: If we want to put something with a data-bs-toggle inside the trigger slot, we are still doing this wrong. Send the triggering element in as a prop?
// Check the dom at runtime for a data-bs-toggle="dropdown" element?
const dropdownTriggerElement = ref<ComponentPublicInstance | null>(null);

/**
 * Bootstrap dropdownInstance
 */
const dropdownInstance = ref<Dropdown | null>(null);

/**
 * Controls rendering of the teleported menu.
 * We must render first, then call Bootstrap show/toggle so _menu is available.
 */
const isShown = ref(false);

/**
 * This is to make sure we dont render outside the viewport.
 */
const maxDropdownHeightInPixels = ref<number>(200); // Default to 200 since that seems like a reasonable number. This will be overwritten on show anyway

/**
 * Get the number of adtion items in the dropdown
 */
const computedDropdowItemsCount = computed(() => {
    if (!props.showItemCountBadge) {
        return 0;
    }

    const groupItemCounterFn = (item: DropdownItemTypes): number => {
        if (item instanceof DropdownItemGroup) {
            return item.items.reduce((acc, innerItem) => acc + groupItemCounterFn(innerItem), 0);
        }
        return 1;
    };

    return (
        props.dropdownItems?.reduce((acc, item) => {
            if (item instanceof DropdownItemSeparator) {
                return 0;
            }
            return groupItemCounterFn(item) + acc;
        }, 0) ?? 0
    );
});

const teleportTarget = computed<HTMLElement | string>(() => {
    const dropdownElement = dropdownelement.value;
    if (!dropdownElement) {
        return 'body';
    }

    // Keep teleported content inside Bootstrap focus-trapped containers like modal/offcanvas.
    // Otherwise Bootstrap _handleFocusin treats dropdown inputs as outside and steals focus.
    const focusTrapContainer = dropdownElement.closest('.modal, .offcanvas');
    if (focusTrapContainer instanceof HTMLElement) {
        return focusTrapContainer;
    }

    return 'body';
});

onMounted(() => {
    if (!dropdownelement.value) {
        console.warn('Dropdown wrapper is not set by the time we reached onMounted. This should never happen.');
        return;
    }

    // Bootstrap cannot reliably locate the teleported menu, so we disable its autoClose and handle
    // outside-click ourselves via handleDocumentMousedown below.
    dropdownInstance.value = Dropdown.getOrCreateInstance(dropdownTriggerElement.value?.$el ?? dropdownelement.value, {
        autoClose: false,
        popperConfig: {
            strategy: 'fixed',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 2],
                        boundary: 'window',
                    },
                },
            ],
        },
    });

    dropdownelement.value.addEventListener('resize', () => {
        dropdownInstance.value?.update();
    });

    dropdownelement.value.addEventListener('hidden.bs.dropdown', () => {
        isShown.value = false;
        emit('hidden');
    });

    dropdownelement.value.addEventListener('shown.bs.dropdown', () => {
        emit('shown');

        const dropdownMenu = dropdownelement.value;
        if (dropdownMenu instanceof HTMLDivElement) {
            const rect = dropdownMenu.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            const spaceBelow = viewportHeight - rect.top - 100; // 100px margin from bottom

            // Set the maxHeight to be the space below the dropdown trigger or 200px minimum
            maxDropdownHeightInPixels.value = spaceBelow < 200 ? 200 : spaceBelow;
        }

        // Reset the scroll position when we open
        // I'm not sure why I have to do this, but if I dont then we start half scrolled for some reason
        if (dropdownMenuUl.value) {
            // Reset scroll position
            dropdownMenuUl.value.scrollTop = 0;
        }

        calculateVirtualScrollerData(false);
        // This is here so we wait untill full render before we try to scroll to the marked item, otherwise we might scroll to the wrong position since the items might not be rendered yet
        window.requestAnimationFrame(() => {
            nextTick(() => {
                scrollToFirstMarkedValue();
            });
        });
    });

    if (props.autoOpen) {
        show();
    }

    document.addEventListener('mousedown', handleDocumentMousedown);
});

onUnmounted(() => {
    document.removeEventListener('mousedown', handleDocumentMousedown);
    // Ensure Teleport content is no longer rendered before teardown.
    isShown.value = false;

    // Close first so Bootstrap can run its normal cleanup while elements still exist.
    // it can already be killed, so we just discard exceptions inside bootstrap here
    try {
        dropdownInstance.value?.hide();
        dropdownInstance.value?.dispose();
    } catch (e) {
        // Ignore errors
    }

    // Safety cleanup: if a teleported menu node still exists in <body>, remove it.
    if (dropdownMenuUl.value?.parentElement) {
        dropdownMenuUl.value.parentElement.removeChild(dropdownMenuUl.value);
    }
});

/**
 * Effective autoClose mode — mirrors the original Bootstrap semantics:
 * - true  : close on any click outside the trigger (default for button lists)
 * - outside: close only when clicking outside the menu (submenus / checkboxes)
 * - inside : close only when clicking inside the menu
 * - false : never auto-close
 */
const effectiveAutoClose = computed(
    () => props.autoClose ?? (props.dropdownItems?.some((t) => t instanceof DropdownItemMenu) ? 'outside' : true),
);

const FOCUSABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A']);

/**
 * Handles mousedown events originating inside the teleported menu.
 * - Always stops propagation so the document-level outside-click handler is never triggered
 *   by clicks that are clearly inside the menu (avoids false positives from timing or
 *   custom #menu slot content not being covered by the dropdownMenuUl containment check).
 * - Prevents the default focus-transfer behaviour for non-focusable targets so the AG Grid
 *   cell editor is not destroyed when the user clicks a non-interactive area of the menu.
 *   Focusable targets (inputs, searchboxes, buttons, etc.) still receive focus normally.
 */
const handleMenuMousedown = (event: MouseEvent) => {
    event.stopPropagation();
    if (event.target instanceof Element && FOCUSABLE_TAGS.has(event.target.tagName)) {
        return false;
    }
    event.preventDefault();
};

/**
 * Document-level mousedown handler that replaces Bootstrap's built-in autoClose.
 * Bootstrap cannot locate the teleported menu via DOM traversal, so we check containment
 * against both the trigger wrapper and the live menu ref ourselves.
 */
const handleDocumentMousedown = (event: MouseEvent) => {
    if (!isShown.value) return;
    if (effectiveAutoClose.value === false || effectiveAutoClose.value === 'inside') return;
    if (!(event.target instanceof Node)) return;

    const target = event.target;
    if (dropdownelement.value?.contains(target)) return;
    if (dropdownMenuUl.value?.contains(target)) return;

    hide();
};

/**
 * Bootstrap Dropdown caches the menu element on a private `_menu` field.
 * Because the menu is teleported to `body` and conditionally rendered, Bootstrap may keep
 * a stale/null reference unless we re-sync it before show/toggle.
 */
const syncDropdownMenuReference = () => {
    if (!dropdownInstance.value || !dropdownMenuUl.value) {
        return;
    }

    Reflect.set(dropdownInstance.value, '_menu', dropdownMenuUl.value);
};

/**
 * Ensures the teleported menu exists in the DOM before calling Bootstrap `show()`.
 * Without this order, Bootstrap may access `_menu.classList` while `_menu` is null.
 */
const show = () => {
    if (!dropdownInstance.value) {
        return;
    }

    if (!isShown.value) {
        isShown.value = true;
        nextTick(() => {
            syncDropdownMenuReference();
            dropdownInstance.value?.show();
        });
        return;
    }

    syncDropdownMenuReference();
    dropdownInstance.value.show();
};

const hide = () => {
    if (dropdownInstance.value) {
        dropdownInstance.value.hide();
    }
};

const onItemActionRun = () => {
    hide();
    emit('action-run');
};

/**
 * Toggle uses the same sync step as show(), since Teleport can recreate the menu node.
 */
const toggle = () => {
    if (!dropdownInstance.value) {
        return;
    }

    if (!isShown.value) {
        show();
        return;
    }

    syncDropdownMenuReference();
    dropdownInstance.value.toggle();
};

const focus = () => {
    const triggerElement = dropdownTriggerElement.value?.$el;
    if (triggerElement instanceof HTMLElement) {
        triggerElement.focus();
        return;
    }

    if (dropdownelement.value instanceof HTMLElement) {
        dropdownelement.value.focus();
    }
};

defineExpose<IDropDownExposed>({
    show,
    hide,
    toggle,
    focus,
});

const emit = defineEmits<{
    /**
     * Emitted when the dropdown is shown
     */
    (e: 'shown'): void;

    /**
     * Emitted when the dropdown is hidden
     */
    (e: 'hidden'): void;

    /**
     * Emitted when a dropdown action item has run.
     * This is re-emitted by submenu instances so parent dropdowns can close as well.
     */
    (e: 'action-run'): void;
}>();

const isDropDownItemHidden = (dropdownItem: DropdownItemTypes) => {
    if (dropdownItem.hideDropDownItem === undefined) {
        return false;
    }
    return dropdownItem.hideDropDownItem.value;
};

const markedItemElements = ref<HTMLElement[]>([]);

onBeforeUpdate(() => {
    markedItemElements.value = [];
});

/**
 * This is used to set the ref for the items that we want to mark, so we can scroll to them and give them a different background color
 * @param element The element of the item
 * @param item The item itself
 */
const setMarkedItemElement = (element: Element | ComponentPublicInstance | null, item: DropdownItemTypes) => {
    if (!props.markValues?.includes(item)) {
        return;
    }

    if (element instanceof HTMLElement) {
        markedItemElements.value.push(element);
        return;
    }

    if (element && '$el' in element && element.$el instanceof HTMLElement) {
        markedItemElements.value.push(element.$el);
    }
};

/**
 * This will find the first index of the dropdown items that is in the markValues and is not hidden.
 * This is used to scroll to the first marked item when we open the dropdown.
 */
const getFirstMarkedDropdownIndex = (): number | undefined => {
    if (!props.dropdownItems?.length || !props.markValues?.length) {
        return undefined;
    }

    for (let index = 0; index < props.dropdownItems.length; index++) {
        const item = props.dropdownItems[index];

        if (isDropDownItemHidden(item)) {
            continue;
        }

        if (props.markValues.includes(item)) {
            return index;
        }

        if (
            item instanceof DropdownItemGroup &&
            item.items.some((innerItem) => !isDropDownItemHidden(innerItem) && props.markValues?.includes(innerItem))
        ) {
            return index;
        }
    }

    return undefined;
};

/**
 * This will scroll to the first marked item in the dropdown.
 * This is used when we open the dropdown to scroll to the currently selected item for example.
 * Goal is to behave as a normal select box
 */
const scrollToFirstMarkedValue = () => {
    if (!dropdownMenuUl.value || !props.markValues?.length) {
        return;
    }

    if (props.virtualScrollerMaxItems !== undefined) {
        const markedIndex = getFirstMarkedDropdownIndex();
        if (markedIndex === undefined) {
            return;
        }

        const markedItemStartPixel = virtualScrollItemStartPixelmap[markedIndex];
        const markedItemHeight = virtualScrollItemPixelHeightMap[markedIndex];
        if (markedItemStartPixel !== undefined) {
            const targetScrollTop = markedItemStartPixel + (markedItemHeight ?? 0) / 2 - dropdownMenuUl.value.clientHeight / 2;
            dropdownMenuUl.value.scrollTop = Math.max(targetScrollTop, 0);
        }
        return;
    }

    const markedElement = markedItemElements.value[0];
    if (markedElement) {
        const menuRect = dropdownMenuUl.value.getBoundingClientRect();
        const markedRect = markedElement.getBoundingClientRect();
        const targetScrollTop =
            dropdownMenuUl.value.scrollTop +
            (markedRect.top - menuRect.top) +
            markedRect.height / 2 -
            dropdownMenuUl.value.clientHeight / 2;
        dropdownMenuUl.value.scrollTop = Math.max(targetScrollTop, 0);
    }
};

const dropdownItemsThatShouldNOTHaveDropdownItemClass = [DropdownItemSeparator, DropdownItemMenu];

const visibleDropdownItems = computed(() => {
    const arr = props.dropdownItems?.filter((i) => !isDropDownItemHidden(i)) ?? [];
    if (props.virtualScrollerMaxItems === undefined) {
        return arr;
    }
    return arr.slice(virtualScrollStartIndex.value, virtualScrollEndIndex.value);
});

// Virtual scroller
// We use a virtual scroller so if we have alot of items in the dropdown, we dont render them all at once

/**
 * The div that contains the virtual scroller items
 * This will move to be positioned about where we have scrolled to
 */
const virtualScrollerScrollDiv = ref<HTMLElement | null>(null);

/**
 * How much to move the virtual scroller div
 */
const virtualScrollerScrollTransitionInPixels = ref(0);

/**
 * The first item that is visible in the virtual scroll window
 */
const virtualScrollStartIndex = ref(0);

/**
 * The last item that is visible in the virtual scroll window
 */
const virtualScrollEndIndex = ref(props.virtualScrollerMaxItems ?? 0);

/**
 * A map of item start pixel positions for each item in the dropdown
 * From this we know where to position the virtual scroller div
 * Note: This assumes that any "special" items are at the start.
 * We use the initially rendered items to find the size of each item.
 * For any item that has not been rendered, we assume that is has the same size as last initially rendered.
 * This will make a big problem for periodic separators for example.
 */
const virtualScrollItemStartPixelmap: number[] = [];

/**
 * The pixel height of each item in the dropdown
 * Note: This might not be accurate. It will be accurate for all the items from 0 to virtualScrollerMaxItems, the rest are the same height as the last rendered item.
 */
const virtualScrollItemPixelHeightMap: number[] = [];

/**
 * The total height of all items.
 * This is so we get a scrollbar that is long enough
 */
const virtualScrollerTotalHeight: Ref<number | null> = ref(null);

/**
 * If any of the items change their visibility then we need to recalculate the total height
 */
if (props.virtualScrollerMaxItems !== undefined) {
    if (props.dropdownItems) {
        watch(
            // Watch each hideDropDownItem ref
            () => props.dropdownItems!.filter((di) => di.hideDropDownItem !== undefined).map((di) => di.hideDropDownItem!.value),
            () => {
                if (!props.dropdownItems) {
                    // Should never happen. What are we watching then?
                    return;
                }

                if (virtualScrollItemPixelHeightMap.length !== props.dropdownItems.length) {
                    // We have not calculated the pixelmap yet
                    // We should probably never get here either
                    console.warn(
                        'Dropdown virtual scroller: Pixelmap length does not match dropdown items length. This means that we are changing the visibility before initial calculation has happened (meaning we havent opened the dropdown yet).',
                    );
                    return;
                }

                let newHeight = 0;
                props.dropdownItems.filter((dropdownitem, index) => {
                    if (!isDropDownItemHidden(dropdownitem)) {
                        // Add the height of this item
                        newHeight += virtualScrollItemPixelHeightMap[index];
                    }
                });
                virtualScrollerTotalHeight.value = newHeight;
            },
        );
    }
}

/**
 * Calculate the virtual scroller data
 * This will calculate the start pixel for each item and the total height of all items
 * @param recalculate If we should recalculate even if we already have data
 */
const calculateVirtualScrollerData = (recalculate: boolean) => {
    // We are not in virtualscroller mode so just exit
    if (props.virtualScrollerMaxItems === undefined) {
        return;
    }
    if (!props.dropdownItems) {
        return;
    }
    if (!virtualScrollerScrollDiv.value) {
        return;
    }
    if (virtualScrollItemStartPixelmap.length > 0 && !recalculate) {
        // Guess we already did this
        return;
    }
    let currentPixel = 0;
    let lastElementHeight = 0;
    // Calculate the start pixel for each item
    for (let i = 0; i < props.dropdownItems.length; i++) {
        // Push what we calculated in the previous iteration
        virtualScrollItemStartPixelmap.push(currentPixel);

        // Find the actual html element
        const element = virtualScrollerScrollDiv.value.children.item(i);

        // If no element we are outside of the rendered window and we should use the last known height
        if (!element) {
            currentPixel += lastElementHeight;

            // Maintain the map of the heights
            virtualScrollItemPixelHeightMap.push(lastElementHeight);
            continue;
        }

        // Use offsetHeight if available, otherwise fallback to clientHeight
        if ('offsetHeight' in element && typeof element.offsetHeight === 'number') {
            lastElementHeight = element.offsetHeight;
        } else {
            console.warn('Element does not have offsetHeight property, using clientHeight instead', element);
            lastElementHeight = element.clientHeight;
        }

        // add to currentpixel so we are ready for the next iteration
        currentPixel += lastElementHeight;

        // Maintain the map of the heights
        virtualScrollItemPixelHeightMap.push(lastElementHeight);
    }

    // Set the totalheight initally (We assume all the items are visible to start with)
    virtualScrollerTotalHeight.value = virtualScrollItemStartPixelmap.at(-1) ?? currentPixel;
};

/**
 * End scroll on the dropdown menu
 * This is used for virtual scrolling
 * @param scrollEvent The scroll event
 */
const dropdownMenuScrollEnd = (scrollEvent: Event) => {
    // We are not in virtualscroller mode so just exit
    if (props.virtualScrollerMaxItems === undefined) {
        return;
    }
    if (!virtualScrollerScrollDiv.value) {
        return;
    }
    const target = scrollEvent.target;
    if (!target || !(target instanceof HTMLElement)) {
        return;
    }
    const scrollTop = target.scrollTop;

    // Find the first item that is below the scrollTop
    let newStartIndex = virtualScrollItemStartPixelmap.findIndex((pixelStart) => pixelStart > scrollTop) - 1;
    if (newStartIndex < 0) {
        newStartIndex = 0;
    }

    virtualScrollStartIndex.value = newStartIndex;
    virtualScrollEndIndex.value = Math.min(newStartIndex + props.virtualScrollerMaxItems, props.dropdownItems?.length ?? 0);

    // We set the translation to the start pixel of the new start index. This gives the smoothest user experience
    virtualScrollerScrollTransitionInPixels.value = virtualScrollItemStartPixelmap[newStartIndex] ?? scrollTop;
    // An alternative is to set it to scrollTop, but if we do that, the first item always starts pixel perfect at the top of where we have scrolled to and stuff "jumps around"
    // Setting it to the items start pixel as above allows us to half scroll an item into view, which is more consistent with how scrolling usually works.
    // virtualScrollerScrollTransitionInPixels.value = scrollTop;
};
</script>
<template>
    <div
        class="eq-dropdown dropdown d-flex"
        :class="classes"
        ref="dropdownelement"
    >
        <slot name="trigger">
            <!-- The triggering button (if not in split mode) -->
            <ButtonComp
                :disabled="props.disabled"
                :icon="icon"
                :icon-position="iconPosition"
                :button-type="buttonType"
                class="flex-grow-1 text-start"
                :class="[$attrs.class]"
                :data-bs-auto-close="autoClose"
                :aria-expanded="isShown"
                textposition="left"
                @click="toggle"
                ref="dropdownTriggerElement"
                :loadstate="loadstate"
                v-if="!splitAction"
            >
                <template v-if="translationkey">{{ $tm(translationkey) }}</template>
                <span
                    v-if="showItemCountBadge"
                    class="badge bg-primary text-white rounded-pill ms-3"
                    >{{ computedDropdowItemsCount }}</span
                >
                <slot name="closedContent"></slot>
                <IconComp
                    :icon="isSubmenu ? 'fas fa-caret-right' : 'fas fa-sort-down'"
                    class="float-end ms-1 text-muted toggleicon"
                    :class="{ 'mt-1': isSubmenu }"
                    v-if="showArrow"
                />
            </ButtonComp>

            <!-- Button and trigger if we are in split mode -->
            <template v-if="splitAction">
                <!-- The trigger button uses the data from the splitAction -->
                <ButtonComp
                    :icon="splitAction.props.icon ?? icon"
                    :icon-position="splitAction.props.iconPosition ?? iconPosition"
                    :button-type="splitAction.props.buttonType ?? buttonType"
                    class="flex-grow-1 text-start"
                    :class="[$attrs.class, splitAction.class]"
                    :data-bs-auto-close="autoClose"
                    textposition="left"
                    @click="splitAction?.action"
                    :disabled="props.disabled ?? splitAction?.props.disabled"
                    :loadstate="loadstate"
                >
                    <template v-if="splitAction.translationkey">{{ $tm(unref(splitAction.translationkey)) }}</template>
                </ButtonComp>
                <!-- the comp can be used with or without dropdownitems, so we only show this if there is items -->
                <template v-if="dropdownItems && dropdownItems?.length > 0">
                    <!-- Since the main button is used for an action, we need a secondary button to open the dropdown -->
                    <div class="vr"></div>
                    <ButtonComp
                        class="dropdown-toggle dropdown-toggle-split no-focus"
                        :button-type="buttonType"
                        :aria-expanded="isShown"
                        @click="toggle"
                        ref="dropdownTriggerElement"
                    >
                        <span class="visually-hidden"></span>
                    </ButtonComp>
                </template>
            </template>
        </slot>
        <!-- This slot is still here because typeahead uses it, but DO NOT USE IT in the future. This will be removed -->
        <slot name="menu">
            <Teleport
                v-if="isShown"
                :to="teleportTarget"
            >
                <!-- The ag-custom-component-popup is needed to make sure ag wont think it has lost focus 
                     when interacting with the teleported dropdown content. 
                     see https://www.ag-grid.com/vue-data-grid/filter-date/#custom-selection-component -->
                <ul
                    class="dropdown-menu ag-custom-component-popup"
                    :class="[
                        props.columns !== undefined && props.columns > 1 ? `dropdown-menu-columns cols-${props.columns ?? 1}` : '',
                        // If we have an actionGroup we have to show the overflow
                        // This means we cant have a long menu with a submenu on the same level.
                        // We do it this way since we cant set overflow-x and overflow-y to different values.
                        // See https://developer.mozilla.org/en-US/docs/Web/CSS/overflow#description
                        dropdownItems?.some((t) => t instanceof DropdownItemMenu) ? 'overflow-visible' : 'overflow-auto',
                    ]"
                    :style="{
                        minWidth: `${dropdownelementWidth}px`,
                        zIndex: 99999999, // Make sure the dropdown is above other elements. We might want to make this customizable in the future
                    }"
                    ref="dropdownMenuUl"
                    @mousedown="handleMenuMousedown"
                    @scrollend="dropdownMenuScrollEnd"
                >
                    <div
                        :style="virtualScrollerTotalHeight !== null ? `min-height: ${virtualScrollerTotalHeight}px` : ''"
                        class="w-100"
                    >
                        <div
                            :style="{
                                transform: `translateY(${virtualScrollerScrollTransitionInPixels}px)`,
                            }"
                            ref="virtualScrollerScrollDiv"
                            class="virtualScrollerScrollDiv"
                        >
                            <template v-if="!dropdownItems?.length">
                                <slot name="emptyDropdown">
                                    <li
                                        class="display-5 muted text-center py-4"
                                        v-if="emptytext"
                                    >
                                        <IconComp
                                            icon="fa-info-circle"
                                            class="text-info"
                                        />
                                        <span v-html="$tm(emptytext)"></span>
                                    </li>
                                </slot>
                            </template>
                            <template v-if="dropdownItems?.length">
                                <template
                                    v-for="dropdownItem in visibleDropdownItems"
                                    :key="dropdownItem.uniqueLoopKey"
                                >
                                    <!-- if its a group we do an inner loop for the group items -->
                                    <template v-if="dropdownItem instanceof DropdownItemGroup">
                                        <span class="dropdown-group">
                                            <template
                                                v-for="innerDropdownItem in dropdownItem.items"
                                                :key="innerDropdownItem.uniqueLoopKey"
                                            >
                                                <li
                                                    v-if="!isDropDownItemHidden(innerDropdownItem)"
                                                    :class="
                                                        dropdownItemsThatShouldNOTHaveDropdownItemClass.some(
                                                            (t) => innerDropdownItem instanceof t,
                                                        )
                                                            ? ''
                                                            : 'dropdown-item'
                                                    "
                                                >
                                                    <DropdownCompItemRenderer
                                                        :dropdown-item="innerDropdownItem"
                                                        :ref="(element) => setMarkedItemElement(element, innerDropdownItem)"
                                                        @action-run="onItemActionRun"
                                                        class="dropdown-item dropend pe-1 me-0"
                                                        :class="[
                                                            innerDropdownItem instanceof DropdownItemSeparator ? 'dropdown-separator' : '',
                                                            markValues?.includes(innerDropdownItem) ? 'bg-secondary' : '',
                                                        ]"
                                                    />
                                                </li>
                                            </template>
                                        </span>
                                    </template>
                                    <!-- Usual items -->
                                    <template v-else>
                                        <li
                                            v-if="!isDropDownItemHidden(dropdownItem)"
                                            :class="
                                                dropdownItemsThatShouldNOTHaveDropdownItemClass.some((t) => dropdownItem instanceof t)
                                                    ? ''
                                                    : ''
                                            "
                                        >
                                            <DropdownCompItemRenderer
                                                :dropdown-item="dropdownItem"
                                                :ref="(element) => setMarkedItemElement(element, dropdownItem)"
                                                @action-run="onItemActionRun"
                                                class="dropdown-item dropend pe-1 me-0"
                                                :class="[
                                                    dropdownItem instanceof DropdownItemSeparator ? 'dropdown-separator' : '',
                                                    markValues?.includes(dropdownItem) ? 'bg-secondary' : '',
                                                ]"
                                            />
                                        </li>
                                    </template>
                                </template>
                            </template>
                        </div>
                    </div>
                </ul>
            </Teleport>
        </slot>
    </div>
</template>
